import React, { useState } from 'react'
import { METRICS, fmt, heatmapRows, idxText, tint } from './benchmarkMath'
import './heatmap.css'

/**
 * Partner x channel index heatmap — React port of benchmark-mock/render/view-heatmap.js.
 * Each cell is the partner's season total indexed against the channel benchmark (mean or median);
 * the Total column is the absolute season value. Owns its sort state; header buttons keep focus
 * across re-sorts because React keeps the same button elements.
 */
export default function Heatmap({ model, metric, scope, stat, partners }) {
    const [sort, setSort] = useState({ column: 'Total', descending: true });
    const selected = new Set(partners);
    const rows = heatmapRows(model, { scope, metric, stat, partners }, sort);
    const pinned = rows.some(row => selected.has(row.partner.name));

    const onSort = column => setSort({ column, descending: sort.column === column ? !sort.descending : true });

    const header = column => {
        const active = sort.column === column;
        const next = active && sort.descending ? 'ascending' : 'descending';
        return (
            <th key={column} scope="col" aria-sort={active ? (sort.descending ? 'descending' : 'ascending') : 'none'}>
                <button type="button" data-column={column} aria-label={'Sort ' + column + ' ' + next} onClick={() => onSort(column)}>
                    {column + (active ? (sort.descending ? ' ↓' : ' ↑') : '')}
                </button>
            </th>
        );
    };

    const cell = (partner, channel, ratio) => {
        if (ratio === null) {
            const row = model.cells.get(partner.name).get(channel);
            const title = row ? 'Benchmark unavailable: n too small or no positive ' + stat : 'No delivery';
            return <td key={channel} data-channel={channel} title={title} className="heatmap-empty">{idxText(ratio)}</td>;
        }
        const value = model.cells.get(partner.name).get(channel)[metric];
        const bench = model.bench[channel][scope][metric];
        const { side, strength } = tint(ratio);
        return (
            <td key={channel} data-channel={channel} data-ratio={String(ratio)}
                className={'heatmap-' + side} style={{ '--heatmap-strength': String(strength) }}
                title={fmt(metric, value) + ' this season; median ' + fmt(metric, bench.median) + ' · mean ' + fmt(metric, bench.mean)}>
                {idxText(ratio)}
            </td>
        );
    };

    return (
        <div className="heatmap-view">
            <div className="heatmap-key">
                <span>
                    {rows.length + ' partners · x1 = channel ' + stat + ' · Total = season total, all channels'
                        + (pinned ? ' · selected partners pinned first' : '')}
                </span>
                <span className="heatmap-legend"
                    aria-label={`Red: below ${stat}; untinted: x1, the ${stat}; green: above ${stat}. Tints cap at x0.25 and x4.`}>
                    <span>x0.25</span><span className="heatmap-gradient" aria-hidden="true" /><span>x4</span>
                </span>
            </div>
            <div className="heatmap-scroll" tabIndex={0} role="region" aria-label="Partner index heatmap; scroll for all partners">
                <table className="hm heatmap-table">
                    <caption className="heatmap-caption">
                        {METRICS[metric] + ' season total, indexed against the channel ' + stat + '. Total is the absolute season value.'}
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Partner</th>
                            {[...model.channels, 'Total'].map(header)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ partner, ratios }) => (
                            <tr key={partner.name} data-partner={partner.name}
                                className={selected.has(partner.name) ? 'selected' : undefined}>
                                <th scope="row">{partner.name}</th>
                                {model.channels.map(channel => cell(partner, channel, ratios[channel]))}
                                <td className="heatmap-total" title={'Total ' + METRICS[metric] + ' this season across all channels'}>
                                    {fmt(metric, partner.total[metric])}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
